export const landmarks = [
  { name: "Orlando Sanford Intl (SFB)", distance: "~10 miles", icon: "ri-flight-takeoff-line", desc: "Regional airport with Allegiant and international charter flights serving Central Florida", url: "https://www.flysfb.com/", schemaType: "Airport" },
  { name: "Orlando International Airport (MCO)", distance: "~35 miles", icon: "ri-flight-takeoff-line", desc: "Major international hub with direct flights to Europe, Latin America, and global destinations", url: "https://orlandoairports.net/", schemaType: "Airport" },
  { name: "Central Florida Zoo", distance: "~5 miles", icon: "ri-ancient-gate-line", desc: "116-acre zoo and botanical gardens with 400+ animals — family destination and regional attraction", url: "https://www.centralfloridazoo.org/", schemaType: "TouristAttraction" },
  { name: "Downtown Lake Mary", distance: "Downtown", icon: "ri-store-line", desc: "Walkable business and dining district with corporate headquarters, restaurants, and boutique shops", url: "https://www.lakemaryfl.com/", schemaType: "TouristAttraction" },
  { name: "I-4", distance: "~2 miles", icon: "ri-road-map-line", desc: "Primary interstate connecting Lake Mary to Orlando, Daytona Beach, and Tampa", url: "https://en.wikipedia.org/wiki/Interstate_4", schemaType: "CivicStructure" },
  { name: "Wekiwa Springs State Park", distance: "~12 miles", icon: "ri-leaf-line", desc: "7,000-acre park with crystal-clear spring for swimming, kayaking, and hiking along the Wekiva River", url: "https://www.floridastateparks.org/wekiwasprings", schemaType: "TouristAttraction" },
];

const cities = [
  { name: "Lake Mary", zips: "32746, 32795" },
  { name: "Sanford", zips: "32771, 32773" },
  { name: "Longwood", zips: "32750, 32779" },
  { name: "Altamonte Springs", zips: "32701, 32714, 32716" },
  { name: "Casselberry", zips: "32707, 32730" },
  { name: "Oviedo", zips: "32765, 32766" },
  { name: "Winter Springs", zips: "32708" },
  { name: "Heathrow", zips: "32746" },
  { name: "Deltona", zips: "32725, 32738" },
  { name: "Debary", zips: "32713" },
  { name: "Orange City", zips: "32763" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from home or your office in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle the rest.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function LakeMaryLocalAreaSection() {
  return (
    <section id="service-area" className="bg-[#F7F8FA] pt-12 md:pt-14 pb-20 md:pb-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Lake Mary &amp; Seminole County
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Whether you are in Lake Mary, Sanford, Longwood, Altamonte Springs, or anywhere in Seminole County and the greater Orlando metro, Fast Passport Center makes expedited passport processing simple — no drive downtown, no agency lines, no guessing.
          </p>
        </div>

        {/* Full-width editorial intro */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-12">
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Lake Mary sits at a unique crossroads in Central Florida. Located just 18 miles north of downtown Orlando along the Interstate 4 corridor, this Seminole County city of roughly 17,000 residents has transformed from a quiet railroad stop into one of the most desirable suburban addresses in the Orlando metropolitan area. But Lake Mary is not merely a bedroom community for Orlando commuters — it is a thriving economic center in its own right, home to the corporate headquarters of AAA (Automobile Club Group), major operations for Deloitte, Verizon Business, and Mitsubishi Power, and a growing technology corridor that draws business travelers from across the country and abroad.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            What distinguishes Lake Mary from other Orlando suburbs is the density of professional, internationally mobile residents. The corporate offices along International Parkway and Lake Emma Road send executives to London for board meetings, to Tokyo for supplier negotiations, and to Frankfurt for energy conferences. The nearby University of Central Florida in Orlando — one of the largest universities in the country — sends students and faculty on study abroad programs, research collaborations, and conference travel. Lake Mary's proximity to both Orlando International Airport (MCO) and Orlando Sanford International Airport (SFB) makes it one of the best-connected suburbs in Florida for international departures.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Lake Mary's residential character is equally distinctive. The city is known for its master-planned communities, golf course neighborhoods, and high-rated Seminole County Public Schools — consistently ranked among the best in Florida. Communities like Heathrow (a gated golf course community), Timacuan, and Alaqua Lakes attract families who value safety, education, and quality of life. The historic downtown Lake Mary along Country Club Road offers a walkable collection of local restaurants, boutiques, and community events that create a small-town atmosphere within a major metro area. The city's trail system connects to the Cross Seminole Trail, giving residents access to miles of biking and walking paths.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Lake Mary's travel demographics break into several overlapping groups. Corporate business travelers — executives, consultants, and technical professionals — fly internationally for meetings, conferences, and project deployments. These trips are often booked with 2–4 weeks of notice and require predictable passport turnaround. Families in Lake Mary's affluent neighborhoods travel to the Caribbean, Europe, and Latin America for vacations, particularly during school breaks. UCF students and young professionals from Lake Mary and nearby Oviedo study abroad in Spain, Italy, and the UK during summer and semester programs. And seasonal residents who own Lake Mary homes may discover passport issues when transitioning between their primary residence and international travel plans.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The geographic reality of Lake Mary also matters. While MCO is only about 30 minutes south on I-4 (or 25 minutes via the 417 toll road), and SFB is just 10 minutes east, there is no passport agency in Seminole County. The closest regional passport agency is in Miami, nearly 250 miles south — a drive that is simply not practical for a Lake Mary professional who discovers an expired passport a week before a London board meeting. Fast Passport Center bridges that gap, allowing Lake Mary travelers to start online, ship overnight from any UPS or FedEx location along I-4, and receive their passport within days rather than months.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            Fast Passport Center has processed expedited passports for Lake Mary travelers across every demographic — from AAA executives flying to European automotive conferences, to families departing on Caribbean cruises from Port Canaveral, to UCF students leaving for Barcelona study abroad programs. Our courier-based expedited service means you start online from your Heathrow home office, your International Parkway corporate campus, or your downtown Lake Mary coffee shop — and your passport arrives within days, not months.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — FPC Office + How It Works */}
          <div>
            {/* FPC Office Card */}
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-building-2-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Orlando</p>
                  <p className="text-white/60 text-xs mt-0.5">200 S Orange Ave, Orlando, FL 32801</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Nearest office to Lake Mary — approximately 20 miles south via I-4
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Courier pickup available throughout Seminole County and Orlando metro</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, choose what works best for you: <strong className="text-white">courier pickup</strong> from your home or office in most cases, <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx location along I-4 or in Sanford, or <strong className="text-white">schedule a drop-off</strong> at our Orlando office. Most clients ship overnight — no visit required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* How FPC Helps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-white rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Cities We Serve + Trust strip */}
          <div>
            <div className="bg-white rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Cities &amp; Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-[#F7F8FA] rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in Seminole County, Orange County, Volusia County, and the greater Orlando metro — start online, ship overnight, and we handle the rest.
              </p>
            </div>

            {/* Internal links */}
            <div className="bg-white rounded-lg p-5 mb-6 border border-gray-200">
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">Nearby Cities We Serve</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: "Orlando", href: "/orlando" },
                  { name: "Winter Park", href: "/winter-park" },
                  { name: "Windermere", href: "/windermere" },
                  { name: "Winter Garden", href: "/winter-garden" },
                  { name: "Celebration", href: "/celebration" },
                  { name: "Tampa", href: "/tampa" },
                ].map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                  >
                    {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Trust credentials strip */}
            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Nearby Landmarks & Airports */}
            <div className="mt-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#F6C344]/15">
                  <i className="ri-landscape-line text-[#1A365D] text-xs"></i>
                </div>
                <h3 className="font-semibold text-[#1A365D] text-sm uppercase tracking-wide">Nearby Landmarks &amp; Airports</h3>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {landmarks.map((item) => (
                  <span key={item.name} className="inline-flex items-center gap-1.5 bg-[#F8F9FB] border border-gray-200/80 rounded-full px-3 py-1.5 hover:border-[#F6C344] transition-colors cursor-default whitespace-nowrap" title={item.desc}>
                    <i className={`${item.icon} text-[#1A365D] text-[10px]`}></i>
                    <span className="text-[#1A365D] font-medium text-[11px]">{item.name}</span>
                    <span className="text-[#B22222] font-semibold text-[10px]">{item.distance}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}